import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { leadsApi, type LeadFilters, type CreateLeadInput } from '@/api/leads';

export const LEADS_KEYS = {
  all: ['leads'] as const,
  list: (filters: LeadFilters) => ['leads', 'list', filters] as const,
  detail: (id: string) => ['leads', 'detail', id] as const,
  stats: () => ['leads', 'stats'] as const,
  activity: (id: string) => ['leads', 'activity', id] as const,
};

export function useLeads(filters: LeadFilters = {}) {
  return useQuery({
    queryKey: LEADS_KEYS.list(filters),
    queryFn: () => leadsApi.getAll(filters),
    placeholderData: (prev) => prev,
  });
}

export function useLeadActivity(id: string, page = 1) {
  return useQuery({
    queryKey: [...LEADS_KEYS.activity(id), page] as const,
    queryFn: () => leadsApi.getActivity(id, page),
    enabled: !!id,
    placeholderData: (prev) => prev,
    staleTime: 0, // always refetch when key changes
  });
}

export function useLeadDetail(id: string) {
  return useQuery({
    queryKey: LEADS_KEYS.detail(id),
    queryFn: () => leadsApi.getById(id),
    enabled: !!id,
  });
}

export function useLeadStats() {
  return useQuery({
    queryKey: LEADS_KEYS.stats(),
    queryFn: leadsApi.getDashboardStats,
    staleTime: 1000 * 60, // 1 min
    refetchInterval: 1000 * 60 * 2, // refresh every 2 min
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLeadInput) => leadsApi.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LEADS_KEYS.all });
      toast.success('Lead created successfully');
    },
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Parameters<typeof leadsApi.update>[1]) =>
      leadsApi.update(id, input),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: LEADS_KEYS.detail(variables.id) });
      void queryClient.invalidateQueries({ queryKey: LEADS_KEYS.activity(variables.id) });
      void queryClient.invalidateQueries({ queryKey: LEADS_KEYS.all });
      toast.success('Lead updated');
    },
  });
}

export function useAddRemark() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, remark, type }: { leadId: string; remark: string; type?: string }) =>
      leadsApi.addRemark(leadId, remark, type),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: LEADS_KEYS.detail(variables.leadId) });
      void queryClient.invalidateQueries({ queryKey: LEADS_KEYS.activity(variables.leadId) });
      toast.success('Note added');
    },
  });
}

export function useAssignLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, toUserId, reason }: { leadId: string; toUserId: string; reason?: string }) =>
      leadsApi.assign(leadId, toUserId, 'manual', reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LEADS_KEYS.all });
      toast.success('Lead assigned');
    },
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: leadsApi.delete,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LEADS_KEYS.all });
      toast.success('Lead deleted');
    },
  });
}
